import { useTranslation } from "react-i18next";

import TradingTimesPage from "../components/tradingTimes/TradingTimes";
import Details from "../components/menu/Details";
import UserManagement from "../components/menu/UserManagement/index";

import AnalyticsDashboardView from "./../container/AnalyticsDashboardView/";
import SquareSyncView from "./../container/SquareSyncView";

import SettingsProfileComponent, {
  IProfileForms,
} from "../components/profile/SettingsProfileComponent";

import { CustomerDetailsContainer } from "../components/customersDetails/CustomerDetailsContainer";

import {
  transformPrepation,
  transformPrepationFloat,
  transformURLs,
  transformOrderTypes,
} from "../utils/transforms";

import preperationForm from "../formSchemas/preperation.form";
import {
  collectionForm,
  deliveryForm,
  dinInForm,
} from "../formSchemas/ordertypes.form";
import collectionLimitsForm from "../formSchemas/collectionLimitsForm.form";
import homeDeliveryForm from "../formSchemas/homeDeliveryForm.form";

import {
  visiblityForm,
  feesForm,
  reviewsForm,
} from "../formSchemas/mysettings.form";
import ReportPage from "../components/reports/ReportPage";
import { IReduxAppState } from "../reducers";
import { useSelector } from "react-redux";
import { MenuContainerTabs } from "../components/Routes";
import "../css/profile.css";
import { ISidebarItem } from "../components/Routes";
import { VenueUrlPage } from "../components/menu/VenueUrlPage";
import { DownloadQRCodePage } from "../components/menu/QrCode/DownloadQRCodePage";
import { ProfileVenueImages } from "../components/profile/ProfileVenueImages";
import { EposNowPage } from "../components/menu/eposnow/EposNow";
import { ICurrency } from "../yoello-lib/modules/redux/actions/localization";
import { useCurrencyState } from "../yoello-lib/modules/hooks/useCurrencyState";
import { DisableRouteComponent } from "../components/util/DisableRouteComponent";
import { useSideTabs } from "../hooks/useSideTabs";
import { urlsForm } from "../formSchemas/urls.form";
import useCheckPermissions from "../hooks/useCheckPermissions";
import { PERMISSIONS } from "../const/permissions.const";

import { NoPermission } from "../components/permissions/NoPermission";
import { useDetailsFilled } from "../hooks/useDetailsFilled";
import { MenuSync } from "../components/menu/menusync/MenuSync";
import { PrintersSettingsContainer } from "../components/printers/PrintersSettingsContainer";
import IVenue from "../interfaces/IVenue";
import { NOT_INCLUDED_VENUES, SHOW_BANNER_VENUES } from "../App";

const MenuContainer = (props: {
  tab: ISidebarItem;
  requiredPermission?: PERMISSIONS;
}): JSX.Element => {
  const position = useSelector(
    (state: IReduxAppState) => state.sidebar.position.key
  );
 
  const { t } = useTranslation();
  useSideTabs(props.tab);

  useDetailsFilled();

  const {
    hasPermission: hasVenuePermission,
    onFailedInteraction,
  } = useCheckPermissions(PERMISSIONS.VENUE_MANAGEMENT);

  const selectedVenueId = useSelector(
    (state: IReduxAppState) => state.selectedVenueId
  );
  const venue = useSelector(
    (state: IReduxAppState) => state.venue[selectedVenueId]?.venueData
  )!;
  const permissions = useSelector((state: IReduxAppState) => state.permissions);

  const currency = useCurrencyState();
  const renderDisplay = () => {
    switch (position) {
      case MenuContainerTabs.ORDER_SETTINGS.key:
        return (
          <>
            <SettingsProfileComponent
              isOrdersSettingPage={true}
              title={venue?.venue_name || "forms.mysettings.title"}
              venueId={venue?.id!}
              profileForms={settingsForm(t, currency?.iso_code === "GBP")}
              isDisabled={hasVenuePermission}
              onFailedInteraction={onFailedInteraction}
            />
            {/* <TippingComponent /> */}
          </>
        );
      case MenuContainerTabs.VENUE_SETTINGS.key:
        return (
          <SettingsProfileComponent
            title={venue?.venue_name || "forms.mysettings.title"}
            venueId={venue?.id!}
            profileForms={venueSettingsForm(currency, venue)}
            isDisabled={!hasVenuePermission}
            onFailedInteraction={onFailedInteraction}
          />
        );
      case MenuContainerTabs.REPORTS.key:
        return <ReportPage venueId={venue?.id!} />;
      case MenuContainerTabs.TRADING_TIMES.key:
        return <TradingTimesPage venueId={venue?.id!} />;
      case MenuContainerTabs.VENUEIMAGES.key:
        return <ProfileVenueImages />;
      case MenuContainerTabs.EXTERNAL_LINKS.key:
        return (
          <SettingsProfileComponent
            venueId={venue?.id!}
            profileForms={linksForm(t)}
            isDisabled={!hasVenuePermission}
            onFailedInteraction={onFailedInteraction}
          />
        );
      case MenuContainerTabs.DETAILS.key:
        return <Details venue={venue} />;
      case MenuContainerTabs.USER_MANAGEMENT.key:
        return <UserManagement />;
      case MenuContainerTabs.VENUE_URL.key:
        return <VenueUrlPage />;
      case MenuContainerTabs.DOWNLOAD_QR_CODE.key:
        return <DownloadQRCodePage />;
      case MenuContainerTabs.EPOSNOW.key:
        return <EposNowPage />;
      case MenuContainerTabs.MENU_SYNC.key:
        return <MenuSync />;
      case MenuContainerTabs.DASHBOARD.key:
        return <AnalyticsDashboardView />;
      case MenuContainerTabs.CUSTOMERS.key:
        return <CustomerDetailsContainer />;
      case MenuContainerTabs.SQUARE_SYNC.key:
        return <SquareSyncView />;
      case MenuContainerTabs.PRINTER_SETTINGS.key:
        return <PrintersSettingsContainer />;
      default:
        return null;
    }
  };

  if (permissions && props.requiredPermission) {
    if (!permissions[props.requiredPermission]) {
      return <NoPermission permission={props.requiredPermission} />;
    }
  }

  return (
    <DisableRouteComponent pageKey={position}>
      <>{renderDisplay()!}</>
    </DisableRouteComponent>
  );
};

const settingsForm = (
  t: (key: string) => string,
  showRadius: boolean
): IProfileForms[] => {
  return [
    {
      transformFunction: transformPrepation,
      formRows: preperationForm(t),
      title: "forms.orders.prep",
      formKey: "preperation",
      formButtonText: "forms.buttons.updateprep",
    },
    {
      transformFunction: transformOrderTypes,
      formRows: dinInForm(t),
      title: "forms.orders.ordertypes",
      formKey: "orderTypes",
      formButtonText: "forms.buttons.updateorder",
    },
    {
      transformFunction: transformOrderTypes,
      formRows: collectionForm(showRadius),
      title: "forms.orders.ordertypes",
      formKey: "orderTypes",
      formButtonText: "forms.buttons.updateorder",
    },
    {
      formRows: collectionLimitsForm,
      title: "",
      formKey: "orderCollectionLimits",
    },
    {
      transformFunction: transformOrderTypes,
      formRows: deliveryForm(showRadius),
      title: "forms.orders.ordertypes",
      formKey: "orderTypes",
      formButtonText: "forms.buttons.updateorder",
    },
    {
      formRows: homeDeliveryForm,
      title: "",
      formKey: "homeDeliveryForm",
    },
  ];
};
const venueSettingsForm = (
  currency: ICurrency | undefined,
  Venue:IVenue
): IProfileForms[] => {
 
  let isDisabled = false;
  if(Venue){
    if(!NOT_INCLUDED_VENUES.includes(Venue.id as string) && SHOW_BANNER_VENUES.includes(Venue?.onboarding_source as unknown as string)){
      isDisabled = true;
    }
  }
  return [
    {
      formRows: visiblityForm(isDisabled),
      title: "forms.mysettings.visibilitytitle",
      formKey: "visibility",
      formButtonText: "forms.mysettings.visiblitybutton",
    },
    {
      transformFunction: transformPrepationFloat,
      formRows: feesForm(currency),
      title: "forms.mysettings.feesTitle",
      formKey: "fees",
      formButtonText: "forms.mysettings.feesButton",
    },
    {
      formRows: reviewsForm(),
      title: "forms.mysettings.reviewsTitle",
      formKey: "visibility",
      formButtonText: "forms.mysettings.reviewsButtonText",
    },
  ];
};

const linksForm = (t: (key: string) => string): IProfileForms[] => {
  return [
    {
      transformFunction: transformURLs,
      formRows: urlsForm(t),
      title: "forms.urls",
      formKey: "custom_links",
      formButtonText: "forms.buttons.updateurls",
    },
  ];
};
export default MenuContainer;
